import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, KeyRound, Link2, RefreshCw, ShieldOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { API_URL, errMsg, fmtDateTime } from './shared';

export const PortalSection = ({ worker, canManage, onChange }) => {
  const [link, setLink] = useState(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const p = worker.portal || {};
  const base = `${API_URL}/godziny/workers/${worker.id}/portal`;

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); onChange(); } catch (e) { toast.error(errMsg(e, 'Operacja nie powiodła się')); }
    finally { setBusy(false); }
  };
  const generate = () => run(async () => {
    const { data } = await axios.post(`${base}/generate`);
    setLink(`${window.location.origin}${data.path}`);
  }, p.active ? 'Wygenerowano nowy link – stary przestał działać' : 'Wygenerowano link');
  const savePin = () => run(async () => { await axios.put(`${base}/pin`, { pin }); setPin(''); }, 'PIN zapisany');
  const revoke = () => run(async () => { await axios.post(`${base}/revoke`); setLink(null); }, 'Link wyłączony');
  const unlock = () => run(() => axios.post(`${base}/unlock`), 'Link odblokowany');
  const copy = async () => { await navigator.clipboard.writeText(link); toast.success('Skopiowano link'); };

  return (
    <div className="rounded-lg border border-[var(--crm-border)] p-3 space-y-3" data-testid={`portal-section-${worker.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold text-[var(--crm-text-primary)]">Portal</span>
        <span className={`px-2 py-0.5 rounded-full text-xs ${p.active && !p.perm_locked ? 'bg-emerald-500/15 text-emerald-500' : 'bg-slate-500/15 text-[var(--crm-text-muted)]'}`}
          data-testid={`portal-status-${worker.id}`}>
          {p.perm_locked ? 'zablokowany' : p.active ? (p.has_pin ? 'aktywny' : 'aktywny – brak PIN-u') : 'wyłączony'}
        </span>
        {p.locked_until && <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/15 text-red-500">zablokowany do {fmtDateTime(p.locked_until)}</span>}
        {p.perm_locked && <span className="px-2 py-0.5 rounded-full text-xs bg-red-600 text-white" data-testid={`portal-perm-locked-${worker.id}`}>zablokowany na stałe</span>}
        <span className="text-xs text-[var(--crm-text-muted)]">ostatnie użycie: {fmtDateTime(p.last_used_at)}</span>
      </div>
      {link && (
        <div className="flex gap-2 items-center bg-amber-500/10 border border-amber-500/30 rounded-md p-2">
          <code className="text-xs break-all flex-1 text-[var(--crm-text-primary)]" data-testid={`portal-link-${worker.id}`}>{link}</code>
          <Button size="sm" variant="outline" onClick={copy} data-testid={`portal-copy-${worker.id}`}><Copy className="w-4 h-4 mr-1" />Kopiuj link</Button>
        </div>
      )}
      {link && <p className="text-xs text-amber-600">Link widać tylko teraz – skopiuj go i wyślij pracownikowi.</p>}
      {canManage && (
        <div className="flex flex-wrap gap-2 items-center">
          <Button size="sm" onClick={generate} disabled={busy || !worker.aktywny} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white"
            data-testid={`portal-generate-${worker.id}`}>
            {p.active ? <><RefreshCw className="w-4 h-4 mr-1" />Wygeneruj nowy</> : <><Link2 className="w-4 h-4 mr-1" />Wygeneruj link</>}
          </Button>
          {p.active && (
            <>
              <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric"
                placeholder="PIN – 6 cyfr" className="w-32 bg-[var(--crm-bg-panel)] border-[var(--crm-border)]" data-testid={`portal-pin-input-${worker.id}`} />
              <Button size="sm" variant="outline" onClick={savePin} disabled={busy || pin.length !== 6} data-testid={`portal-pin-save-${worker.id}`}>
                <KeyRound className="w-4 h-4 mr-1" />{p.has_pin ? 'Zmień PIN' : 'Ustaw PIN'}
              </Button>
              {(p.perm_locked || p.locked_until) && (
                <Button size="sm" variant="outline" onClick={unlock} disabled={busy} data-testid={`portal-unlock-${worker.id}`}>Odblokuj</Button>
              )}
              <Button size="sm" variant="outline" onClick={revoke} disabled={busy} className="text-red-500" data-testid={`portal-revoke-${worker.id}`}>
                <ShieldOff className="w-4 h-4 mr-1" />Wyłącz
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
